import type { Earning } from "@biteasy/contracts";
import { skipToken, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DollarSign, Download } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CopyableId } from "@/components/CopyableId";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import { Heading } from "@/components/catalyst/Heading";
import {
	Pagination,
	PaginationNext,
	PaginationPrevious,
} from "@/components/catalyst/Pagination";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { Time } from "@/components/Time";
import { orpc } from "@/orpc/client";
import { downloadCSV, formatCurrency, generateCSV } from "@/util/format";
import { type EarningsFilters, EarningsFiltersBar } from "./EarningsFiltersBar";

const EARNING_STATUS_CONFIG = {
	accrued: { color: "emerald", label: "Accrued" },
	processing: { color: "blue", label: "Processing" },
	paid: { color: "sky", label: "Paid" },
	void: { color: "zinc", label: "Void" },
} as const;

/**
 * Generate CSV content from earnings data
 */
function generateEarningsCSV(items: Earning[]): string {
	const headers = [
		"ID",
		"App",
		"Partner",
		"Amount",
		"Currency",
		"Status",
		"Transaction ID",
		"Install ID",
		"Payout ID",
		"Period Start",
		"Period End",
		"Created At",
	];

	const rows = items.map((item) => [
		item.id,
		item.appName,
		item.partnerName,
		(item.amountCents / 100).toFixed(2),
		item.status,
		item.transactionId,
		item.installId,
		item.payoutId ?? "",
		item.periodStart?.toISOString() ?? "",
		item.periodEnd?.toISOString() ?? "",
		item.createdAt.toISOString(),
	]);

	return generateCSV(headers, rows);
}

export function Earnings() {
	const navigate = useNavigate();
	const { ec: cursors } = useSearch({ from: "/_app/finances/" });
	const setCursors = useCallback(
		(updater: string[] | ((prev: string[]) => string[])) => {
			navigate({
				to: "/finances",
				search: (prev) => ({
					...prev,
					tab: prev.tab as "earnings" | "payouts" | "payment_methods",
					ec:
						typeof updater === "function"
							? updater((prev.ec as string[]) ?? [])
							: updater,
				}),
			});
		},
		[navigate],
	);
	const [filters, setFilters] = useState<EarningsFilters>({});
	const [isExporting, setIsExporting] = useState(false);
	const [exportComplete, setExportComplete] = useState(false);

	const currentCursor =
		cursors.length > 0 ? cursors[cursors.length - 1] : undefined;

	const { data } = useSuspenseQuery(
		orpc.earnings.list.queryOptions({
			input: {
				limit: 20,
				cursor: currentCursor,
				appId: filters.appId,
				partnerId: filters.partnerId,
				status: filters.status,
				amountMin: filters.amountMin,
				amountMax: filters.amountMax,
				createdAfter: filters.createdAfter,
				createdBefore: filters.createdBefore,
			},
		}),
	);

	// Streaming export query using oRPC's streamedOptions
	const exportQuery = useQuery(
		orpc.earnings.export.experimental_streamedOptions({
			input: isExporting
				? {
						appId: filters.appId,
						partnerId: filters.partnerId,
						status: filters.status,
						amountMin: filters.amountMin,
						amountMax: filters.amountMax,
						createdAfter: filters.createdAfter,
						createdBefore: filters.createdBefore,
					}
				: skipToken,
			queryFnOptions: {
				refetchMode: "reset",
			},
			retry: true,
		}),
	);

	// Handle export completion
	useEffect(() => {
		if (
			isExporting &&
			exportQuery.data &&
			exportQuery.fetchStatus === "idle" &&
			!exportQuery.isFetching &&
			!exportComplete
		) {
			const items = exportQuery.data;
			const csv = generateEarningsCSV(items);
			const timestamp = new Date().toISOString().split("T")[0];
			downloadCSV(csv, `earnings-export-${timestamp}.csv`);
			toast.success(`Exported ${items.length} earnings`);
			setIsExporting(false);
			setExportComplete(true);
		}
	}, [
		isExporting,
		exportQuery.data,
		exportQuery.fetchStatus,
		exportQuery.isFetching,
		exportComplete,
	]);

	const handleExport = () => {
		setExportComplete(false);
		setIsExporting(true);
	};

	const handleFiltersChange = useCallback(
		(newFilters: EarningsFilters) => {
			setFilters(newFilters);
			// Reset pagination when filters change
			setCursors([]);
			// Reset export state when filters change
			setExportComplete(false);
		},
		[setCursors],
	);

	const handleNext = () => {
		if (data.nextCursor) {
			setCursors((prev) => [...prev, data.nextCursor ?? ""]);
		}
	};

	const handlePrevious = () => {
		if (cursors.length > 0) {
			setCursors((prev) => prev.slice(0, -1));
		}
	};

	const hasPrevious = cursors.length > 0;
	const hasNext = !!data.nextCursor;

	return (
		<div>
			<div className="flex items-center justify-between">
				<Heading level={2}>Partner Earnings</Heading>
				<Button
					outline
					onClick={handleExport}
					disabled={isExporting || exportQuery.isFetching}
				>
					<Download className="size-4 mr-1" />
					{isExporting || exportQuery.isFetching
						? `Exporting${exportQuery.data ? ` (${exportQuery.data.length})` : "..."}`
						: "Export CSV"}
				</Button>
			</div>

			<EarningsFiltersBar onFiltersChange={handleFiltersChange} />

			{data.totalCount > 0 && (
				<p className="mt-4 text-sm text-base-content/60 text-right">
					{data.totalCount.toLocaleString()} earning
					{data.totalCount !== 1 ? "s" : ""} recorded
				</p>
			)}

			{data.items.length === 0 ? (
				<div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50 px-6 py-16">
					<div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
						<DollarSign className="size-8 text-primary" />
					</div>
					<h3 className="mt-4 text-lg font-semibold text-base-content">
						No earnings found
					</h3>
					<p className="mt-2 max-w-sm text-center text-sm text-base-content/60">
						{Object.keys(filters).length > 0
							? "Try adjusting your filters to see more results."
							: "Partner earnings will appear here when purchases are attributed to your referral partners."}
					</p>
				</div>
			) : (
				<>
					<Table className="mt-8">
						<TableHead>
							<TableRow>
								<TableHeader>App</TableHeader>
								<TableHeader>Partner</TableHeader>
								<TableHeader>Amount</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Transaction</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map((earning) => {
								const statusConfig = EARNING_STATUS_CONFIG[earning.status] ?? {
									color: "zinc",
									label: earning.status,
								};
								return (
									<TableRow key={earning.id}>
										<TableCell className="font-medium text-base-content">
											{earning.appName}
										</TableCell>
										<TableCell className="text-base-content/60">
											{earning.partnerName}
										</TableCell>
										<TableCell className="font-medium text-base-content">
											{formatCurrency(earning.amountCents, "USD")}
										</TableCell>
										<TableCell>
											<Badge color={statusConfig.color}>
												{statusConfig.label}
											</Badge>
										</TableCell>
										<TableCell className="text-base-content/60">
											<CopyableId value={earning.transactionId} />
										</TableCell>
										<TableCell className="text-base-content/60">
											<Time date={earning.createdAt} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					<Pagination className="mt-4">
						<PaginationPrevious
							onClick={hasPrevious ? handlePrevious : undefined}
							className={
								!hasPrevious
									? "pointer-events-none opacity-50"
									: "cursor-pointer!"
							}
							href={hasPrevious ? undefined : "#"}
						/>
						<PaginationNext
							onClick={hasNext ? handleNext : undefined}
							className={
								!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer!"
							}
							href={hasNext ? undefined : "#"}
						/>
					</Pagination>
				</>
			)}
		</div>
	);
}
